/* Admin → Help Manual (iter-36)
 *
 * Edit the in-app user manual without a code push.
 * Each section has metadata (id, title, icon, summary, order) and a list
 * of typed blocks (para / list / step / tip / code). The editor renders
 * a left list of sections and a right pane with the section's blocks
 * inline-editable, plus reorder up/down arrows and a delete button.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, BookOpen, FileText, ListChecks, ChevronRight,
  Megaphone, Clock, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { helpAdminApi } from '@/lib/api';
import { PanelHeader } from './_shared';


// iter55u — helpers + BlockEditor moved to a sibling file.
import { BLOCK_KINDS, fmtRelative, _nextLid, newBlock, BlockEditor } from "./HelpManualBlockEditor";
import HelpDrawerPanel from './HelpDrawerPanel';

export default function HelpManualPanel() {
  const [mode, setMode] = useState('manual'); // 'manual' | 'drawer'
  const [meta, setMeta] = useState({ title: '', subtitle: '', version: '' });
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingFocusId, setPendingFocusId] = useState('');
  const idInputRef = useRef(null);
  // iter-40 — broadcast indicator + force-send dialog
  const [broadcast, setBroadcast] = useState(null);   // {recipient_count, last_sent_at, last_summary, pending_summary}
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await helpAdminApi.get();
      setMeta(r.meta || {});
      // iter-53h — Backfill `_lid` for blocks loaded from the server so
      // every BlockEditor row has a stable key even before the user
      // edits the section.
      const withLids = (r.sections || []).map((s) => ({
        ...s,
        blocks: (s.blocks || []).map((b) => ({ ...b, _lid: b._lid || _nextLid() })),
      }));
      setSections(withLids);
      if (withLids[0]) setActiveId((prev) => prev || withLids[0].id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load manual.');
    } finally { setBusy(false); }
  }, []);

  const reloadBroadcast = useCallback(async () => {
    try { setBroadcast(await helpAdminApi.broadcastStatus()); }
    catch (err) {
      // Non-fatal — the broadcast indicator simply stays hidden. Trace at
      // debug level so unexpected backend errors are still visible in devtools.
      console.debug('[help/broadcast-status] fetch failed:', err);
    }
  }, []);

  useEffect(() => { reload(); reloadBroadcast(); }, [reload, reloadBroadcast]);

  const notifyNow = async () => {
    setBroadcasting(true);
    try {
      const r = await helpAdminApi.broadcastNow();
      toast.success(`Broadcast queued to ${r.recipient_count} user${r.recipient_count === 1 ? '' : 's'}.`);
      reloadBroadcast();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Broadcast failed.');
    } finally {
      setBroadcasting(false);
      setBroadcastDialogOpen(false);
    }
  };

  const active = sections.find((s) => s.id === activeId) || null;

  const updateActive = (patch) => {
    setSections(prev => prev.map((s) => s.id === activeId ? { ...s, ...patch } : s));
    // iter-38 — if the id changed, follow it so the editor stays bound.
    if (patch.id && patch.id !== activeId) setActiveId(patch.id);
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      await helpAdminApi.saveMeta(meta);
      toast.success('Manual title/subtitle saved.');
      reloadBroadcast();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const saveSection = async () => {
    if (!active) return;
    setBusy(true);
    try {
      // iter-53h — Strip the transient `_lid` markers before sending so
      // the backend's strict Pydantic model doesn't choke on them.
      const payload = {
        ...active,
        blocks: (active.blocks || []).map(({ _lid, ...rest }) => rest),
      };
      await helpAdminApi.upsertSection(active.id, payload);
      toast.success(`Section “${active.title}” saved.`);
      reload();        // pull back the server's updated_at
      reloadBroadcast();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const deleteSection = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await helpAdminApi.deleteSection(active.id);
      toast.success('Section removed.');
      setActiveId('');
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed.'); }
    finally { setBusy(false); }
  };

  const addSection = () => {
    if (busy) return;  // iter-37 — block while reload() is still resolving
    const sid = `new_${Date.now().toString(36)}`;
    setSections((prev) => {
      const next = { id: sid, title: 'New section', icon: 'BookOpen', summary: '', order: prev.length, blocks: [{ kind: 'para', text: 'Write the section intro here.' }] };
      return [...prev, next];
    });
    setActiveId(sid);
    setPendingFocusId(sid);  // iter-39 — focus the ID input once mounted
  };

  // iter-39 — when a fresh section is added, auto-focus + select its ID input
  // so the user can rename it immediately. Uses a "pending" id so we run the
  // effect after the active section actually renders.
  useEffect(() => {
    if (!pendingFocusId || activeId !== pendingFocusId) return;
    if (idInputRef.current) {
      idInputRef.current.focus();
      idInputRef.current.select();
    }
    setPendingFocusId('');
  }, [pendingFocusId, activeId]);

  const reorder = async (delta) => {
    const i = sections.findIndex((s) => s.id === activeId);
    if (i < 0) return;
    const j = i + delta;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
    try { await helpAdminApi.reorder(next.map((s) => s.id)); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Reorder failed.'); }
  };

  const resetDefaults = async () => {
    setBusy(true);
    try {
      await helpAdminApi.resetDefaults();
      toast.success('Manual restored to defaults.');
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Reset failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-help-panel">
      <PanelHeader
        title="Help Manual"
        subtitle="Edit the in-app user manual without a code push. Every change is reflected immediately in the Help → The Loom Manual dialog and in the downloadable PDF."
        onRefresh={reload}
      />

      {/* Mode toggle */}
      <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1 w-fit" data-testid="help-mode-toggle">
        <button
          type="button"
          onClick={() => setMode('manual')}
          className={`rounded-md px-3 py-1 text-[12px] font-medium transition-colors ${mode === 'manual' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="help-mode-manual"
        >
          Manual Sections
        </button>
        <button
          type="button"
          onClick={() => setMode('drawer')}
          className={`rounded-md px-3 py-1 text-[12px] font-medium transition-colors ${mode === 'drawer' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="help-mode-drawer"
        >
          Drawer Tips
        </button>
      </div>

      {mode === 'drawer' ? (
        <HelpDrawerPanel />
      ) : (
        <>

      {/* Manual metadata */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold"><FileText className="h-4 w-4" /> Manual metadata</div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-[11px]">Title</Label>
            <Input value={meta.title || ''} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                    data-testid="help-meta-title" />
          </div>
          <div>
            <Label className="text-[11px]">Subtitle</Label>
            <Input value={meta.subtitle || ''} onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
                    data-testid="help-meta-subtitle" />
          </div>
          <div>
            <Label className="text-[11px]">Version</Label>
            <Input value={meta.version || ''} onChange={(e) => setMeta({ ...meta, version: e.target.value })}
                    data-testid="help-meta-version" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="gap-1.5" data-testid="help-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Restore defaults
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restore the manual to defaults?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every section will be wiped and re-seeded from the in-code default. Custom edits will be lost.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetDefaults} data-testid="help-reset-confirm">Restore</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button size="sm" onClick={saveMeta} disabled={busy} className="gap-1.5" data-testid="help-meta-save">
            <Save className="h-3.5 w-3.5" /> Save metadata
          </Button>
        </div>
      </div>

      {/* iter-40 — Broadcast indicator + force-send */}
      <div className="flex items-center justify-between rounded-md border border-border bg-card px-4 py-2.5 shadow-panel"
            data-testid="help-broadcast-card">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Megaphone className="h-3.5 w-3.5" />
          </div>
          <div className="text-[12px] leading-tight">
            <div className="font-semibold">Email broadcast</div>
            <div className="text-muted-foreground" data-testid="help-broadcast-status">
              {broadcast === null ? 'Loading…'
                : broadcast.last_sent_at
                  ? <>Last sent <span className="text-foreground">{fmtRelative(broadcast.last_sent_at)}</span> · {broadcast.recipient_count} recipient{broadcast.recipient_count === 1 ? '' : 's'}</>
                  : <>No broadcasts yet · {broadcast.recipient_count} recipient{broadcast.recipient_count === 1 ? '' : 's'} will be notified</>}
            </div>
          </div>
        </div>
        <AlertDialog open={broadcastDialogOpen} onOpenChange={setBroadcastDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5"
                    disabled={!broadcast || broadcasting} data-testid="help-broadcast-btn">
              {broadcasting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5" />}
              Notify all users now
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send broadcast email now?</AlertDialogTitle>
              <AlertDialogDescription>
                This bypasses the 1-hour debounce and emails <b>{broadcast?.recipient_count ?? '…'}</b> user
                {broadcast?.recipient_count === 1 ? '' : 's'} immediately with a &ldquo;Manual updated&rdquo; note.
                Use this when you&apos;ve just shipped a major manual update and want everyone to know right away.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={notifyNow} data-testid="help-broadcast-confirm">Send now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Sections editor */}
      <div className="grid grid-cols-12 gap-3">
        {/* Section list */}
        <div className="col-span-12 sm:col-span-4 rounded-md border border-border bg-card p-2 shadow-panel">
          <div className="flex items-center justify-between px-1 pb-1.5">
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold"><ListChecks className="h-3.5 w-3.5" /> Sections</div>
            <Button size="sm" variant="outline" className="h-7 gap-1" onClick={addSection} disabled={busy} data-testid="help-add-section">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {sections.map((s) => (
              <button key={s.id} type="button"
                       onClick={() => setActiveId(s.id)}
                       data-testid={`help-section-toc-${s.id}`}
                       className={`w-full text-left px-2 py-1.5 rounded-sm text-[12.5px] flex items-center justify-between gap-2 ${activeId === s.id ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-secondary'}`}>
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{s.title || s.id}</span>
                  {s.updated_at && (
                    <span className="mt-0.5 flex items-center gap-1 text-[10px] font-normal text-muted-foreground/80"
                          data-testid={`help-section-updated-${s.id}`}>
                      <Clock className="h-2.5 w-2.5" /> {fmtRelative(s.updated_at)}
                    </span>
                  )}
                </span>
                <ChevronRight className="h-3 w-3 shrink-0" />
              </button>
            ))}
            {sections.length === 0 && (
              <div className="px-2 py-4 text-center text-[11.5px] text-muted-foreground">No sections — click Add.</div>
            )}
          </div>
        </div>

        {/* Active section editor */}
        <div className="col-span-12 sm:col-span-8 rounded-md border border-border bg-card p-3 shadow-panel space-y-3" data-testid="help-section-editor">
          {!active ? (
            <div className="text-[12px] text-muted-foreground p-4">Pick a section on the left to edit it.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <Label className="text-[11px]">ID (URL fragment)</Label>
                  <Input value={active.id} onChange={(e) => updateActive({ id: e.target.value })}
                          ref={idInputRef}
                          data-testid="help-edit-id" />
                </div>
                <div>
                  <Label className="text-[11px]">Title</Label>
                  <Input value={active.title} onChange={(e) => updateActive({ title: e.target.value })}
                          data-testid="help-edit-title" />
                </div>
                <div>
                  <Label className="text-[11px]">Icon (lucide name)</Label>
                  <Input value={active.icon} onChange={(e) => updateActive({ icon: e.target.value })}
                          data-testid="help-edit-icon" />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Summary (sidebar caption)</Label>
                <Input value={active.summary || ''} onChange={(e) => updateActive({ summary: e.target.value })}
                        data-testid="help-edit-summary" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px]">Content blocks</Label>
                  <Button size="sm" variant="outline" className="h-7 gap-1"
                           onClick={() => updateActive({ blocks: [...(active.blocks || []), newBlock('para')] })}
                           data-testid="help-add-block">
                    <Plus className="h-3 w-3" /> Add block
                  </Button>
                </div>
                {(active.blocks || []).map((b, i) => (
                  <BlockEditor
                    key={b._lid || `idx-${i}`} block={b}
                    onChange={(next) => {
                      const arr = [...active.blocks]; arr[i] = { ...next, _lid: b._lid }; updateActive({ blocks: arr });
                    }}
                    onRemove={() => {
                      const arr = active.blocks.filter((_, j) => j !== i); updateActive({ blocks: arr });
                    }}
                    onMoveUp={() => {
                      if (i === 0) return;
                      const arr = [...active.blocks];
                      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                      updateActive({ blocks: arr });
                    }}
                    onMoveDown={() => {
                      if (i >= active.blocks.length - 1) return;
                      const arr = [...active.blocks];
                      [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                      updateActive({ blocks: arr });
                    }}
                  />
                ))}
                {(!active.blocks || active.blocks.length === 0) && (
                  <div className="rounded-sm border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
                    No blocks. Click Add block to start.
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-border">
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => reorder(-1)} data-testid="help-section-up">
                    <ArrowUp className="h-3.5 w-3.5" /> Move up
                  </Button>
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => reorder(1)} data-testid="help-section-down">
                    <ArrowDown className="h-3.5 w-3.5" /> Move down
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-destructive gap-1.5" data-testid="help-section-delete">
                        <Trash2 className="h-3.5 w-3.5" /> Delete section
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete “{active.title}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes the section permanently. You can restore from defaults later if needed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteSection} data-testid="help-section-delete-confirm">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button size="sm" onClick={saveSection} disabled={busy} className="gap-1.5" data-testid="help-section-save">
                    <Save className="h-3.5 w-3.5" /> Save section
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
